'use client'

import { MouseEvent, useEffect } from 'react'
import classNames from 'classnames'
import { Button, Cell, Spacer, Text, Divider } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import {
  StatusStyle,
  ConversationLinkActions,
  ConversationMessage,
  StatusMessage as StatusMessageType,
  clickEvent,
} from '@marketplace-web/messaging/conversation-messaging-data'
import {
  escrowOrderDetailsActionLatencyViewEvent,
  escrowOrderDetailsClickEvent,
  EscrowOrderDetailsClickTarget,
  EscrowOrderTrackingEntrySource,
} from '@marketplace-web/order-details/order-details-data'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import { ORDER_DETAILS_URL } from '../../../constants/routes'
import MessageTime from '../../MessageTime'
import LinkAction from '../../ConversationLinkAction/LinkAction'
import { ConversationAction } from '../../ConversationActions'
import { getAnchorClassNameMatch } from '../../../utils/regex'

type OwnProps = {
  transactionId?: number
  message: StatusMessageType
  toggleSidebar?: () => void
}

type Props = OwnProps & ConversationMessage

const StatusMessage = ({ transactionId, message, createdTime }: Props) => {
  const isCesModalVisibilityFixEnabled = useFeatureSwitch('confirm_resolved_modal_fix')
  const { track } = useTracking()
  const translate = useTranslate('conversation.actions.order_details')
  const breakpoints = useBreakpoint()
  const { conversation, transaction, conversationLoadStartForEscrowOrderABTest } =
    useConversationContext()

  const transactionStatus = conversation?.transaction?.status
  const shipmentId = conversation?.transaction?.shipment?.id
  const shipmentStatus = conversation?.transaction?.shipment?.status

  const isActiveStatus = message.style !== StatusStyle.Clear
  const isActionMessage = !!message.actions
  const isWarningActiveStatus = message.style === StatusStyle.Red

  const renderOrderDetailsAction = conversation?.orderDetailsVariant === 'c'

  useEffect(() => {
    if (!transaction || !message.actions) return

    const latency = new Date().getTime() - conversationLoadStartForEscrowOrderABTest

    if (latency < 0) return

    const messageActions =
      conversation?.orderDetailsVariant === 'c'
        ? []
        : message.actions?.map(({ action }) => action) || []

    track(
      escrowOrderDetailsActionLatencyViewEvent({
        screen: 'message_reply',
        escrow_order_id: transaction.id.toString(),
        actions: messageActions.length > 0 ? messageActions : null,
        latency_ms: latency.toString(),
        details: JSON.stringify({
          transaction_status: transactionStatus,
          shipment_status: shipmentStatus,
        }),
      }),
    )
  }, [
    conversation,
    conversationLoadStartForEscrowOrderABTest,
    message.actions,
    track,
    transaction,
    transactionStatus,
    shipmentStatus,
  ])

  const getLinkAction = (text: string | null) => {
    const availableActions = Object.values(ConversationLinkActions).join('|')

    return getAnchorClassNameMatch(text, availableActions)
  }

  const handleTrackingLinkClick = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return

    const targetDetails = {
      transaction_id: transactionId,
      url: event.target.href,
      transaction_status: transactionStatus,
      shipment_id: shipmentId,
      shipment_status: shipmentStatus,
    }

    const trackingEvent = clickEvent({
      screen: 'message_reply',
      target: 'hyperlink',
      targetDetails: JSON.stringify(targetDetails),
    })

    track(trackingEvent)
  }

  const getButtonTheme = () => {
    if (isWarningActiveStatus) return 'warning'

    return undefined
  }

  const getContentTheme = () => {
    if (!isActionMessage) return 'muted'

    return undefined
  }

  const getTitleTheme = () => {
    if (isWarningActiveStatus && isActionMessage) return 'warning'

    if (!isActionMessage) return 'muted'

    return undefined
  }

  const { title, subtitle, actions } = message

  const textTheme = getContentTheme()
  const titleTheme = getTitleTheme()

  const getSubtitle = () => {
    if (renderOrderDetailsAction && isActionMessage)
      return <Text as="span" html text={translate('subtitle')} format />

    return subtitle ? <Text as="span" theme={textTheme} html text={subtitle} format /> : null
  }

  const renderOrderDetailsNavigation = () => {
    if (!transaction) return null

    const handleClick = () => {
      track(
        escrowOrderDetailsClickEvent({
          screen: 'message_reply',
          escrow_order_id: transaction.id.toString(),
          user_side: transaction.userSide || '',
          target: EscrowOrderDetailsClickTarget.ShowEscrowOrderDetails,
          details: JSON.stringify({
            transaction_status: transaction.status,
          }),
        }),
      )
    }

    return (
      <>
        <Spacer size={Spacer.Size.Large} />
        <div className={breakpoints.phones ? '' : 'u-flexbox'}>
          <div>
            <ConversationActionButton
              text={translate('button')}
              size={Button.Size.Medium}
              theme={getButtonTheme()}
              primary
              url={ORDER_DETAILS_URL(transaction.id, EscrowOrderTrackingEntrySource.Conversation)}
              onClick={handleClick}
              testId="conversation-status-order-details-navigation"
            />
          </div>
        </div>
      </>
    )
  }

  const renderActions = () => {
    if (renderOrderDetailsAction && isActionMessage) return renderOrderDetailsNavigation()

    if (!actions) return null

    return (
      actions?.length > 0 && (
        <>
          <Spacer size={Spacer.Size.Large} />
          <div className={breakpoints.phones ? '' : 'u-flexbox'}>
            <SeparatedList
              separator={
                breakpoints.phones ? (
                  <Spacer />
                ) : (
                  <div>
                    <Spacer />
                  </div>
                )
              }
            >
              {actions.map((action, index) => (
                <div key={index} data-testid={`conversation-status-actions-${action.action}`}>
                  <ConversationAction
                    {...action}
                    size={Button.Size.Medium}
                    theme={getButtonTheme()}
                    isCesModalVisibilityFixEnabled={isCesModalVisibilityFixEnabled}
                  />
                </div>
              ))}
            </SeparatedList>
          </div>
        </>
      )
    )
  }

  const renderContent = () => {
    return (
      <Cell>
        <Cell
          styling={Cell.Styling.Tight}
          title={
            title ? <Text as="h2" type={Text.Type.Title} theme={titleTheme} text={title} /> : null
          }
          body={
            <>
              {getSubtitle()}
              {renderActions()}
            </>
          }
          testId="conversation-message--status-message"
        />
      </Cell>
    )
  }

  const renderLinkAction = () => {
    const linkAction = getLinkAction(message.subtitle)

    if (!linkAction) return null

    return <LinkAction action={linkAction} />
  }

  const statusClass = classNames('conversation-message__status', {
    'u-sticky-top u-zindex-bump': isActiveStatus,
  })

  return (
    <>
      <MessageTime time={createdTime} />
      <div className={statusClass}>
        <Divider />
        <span role="none" onClick={handleTrackingLinkClick}>
          {renderContent()}
        </span>
        <Divider />
      </div>
      {renderLinkAction()}
    </>
  )
}

export default StatusMessage
